import { keepPreviousData, useQueryClient } from "@tanstack/react-query";

import { useFetchMutation, useFetchPrefetch, useFetchQuery } from "common/queries/hooks";
import { FetchError } from "common/fetch/errors";
import { IPaginatedQuery } from "common/types";
import { PaymentErrorData } from "common/types/payments";
import { WithStringValues } from "common/utils/types";

import {
  BasicSubscription,
  BasicSubscriptionsSearch,
  DetailSubscription,
  OnboardingDetailSubscription,
  SubscriptionUpdate,
  SubscriptionPaymentUpdate,
  SubscriptionPaymentUpdateResponse,
  BulkUpdateSubscriptions,
  BulkUpdateSubscriptionsResponse,
  ListSubscription,
  PaginatedSubscriptionsSearch,
  SubscriptionListSearch,
  RetentionOfferPreview,
  RetentionOfferDecision,
  RetentionOfferDecisionUpdate,
} from "../types";
import { keys, urls } from "./constants";
import { useInvalidateClient } from "../../accounts/hooks/invalidate";

const noop = () => undefined;

export const useSubscriptionsQuery = (
  search: WithStringValues<SubscriptionListSearch> = {},
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ListSubscription[], WithStringValues<SubscriptionListSearch>>(
    keys.subscriptionList(search),
    {
      fetch: { url: urls.subscriptions(), body: search },
      config: { placeholderData: keepPreviousData, enabled },
    },
  );
};

export const usePaginatedBasicSubscriptionsQuery = (
  search: WithStringValues<BasicSubscriptionsSearch> = {},
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<
    IPaginatedQuery<BasicSubscription>,
    WithStringValues<BasicSubscriptionsSearch>
  >(keys.subscriptionBasicList(search), {
    fetch: { url: urls.basicSubscriptions(), body: search },
    config: { placeholderData: keepPreviousData, enabled },
  });
};

export const usePrefetchPaginatedBasicSubscriptions = () => {
  const prefetch = useFetchPrefetch<
    IPaginatedQuery<BasicSubscription>,
    WithStringValues<BasicSubscriptionsSearch>
  >();
  return (search: WithStringValues<BasicSubscriptionsSearch>) =>
    prefetch(keys.subscriptionBasicList(search), {
      fetch: { url: urls.basicSubscriptions(), body: search },
    });
};

export const usePaginatedSubscriptionsQuery = (
  search: PaginatedSubscriptionsSearch = {},
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<IPaginatedQuery<ListSubscription>, PaginatedSubscriptionsSearch>(
    keys.subscriptionListPaginated(search),
    {
      fetch: { url: urls.subscriptionsPaginated(), body: search },
      config: { placeholderData: keepPreviousData, enabled },
    },
  );
};

export const useSubscriptionQuery = (id: number) => {
  return useFetchQuery<DetailSubscription>(keys.subscriptionDetail(id), {
    fetch: { url: urls.subscription(id) },
  });
};

export const useOnboardingSubscriptionQuery = (id: number) => {
  return useFetchQuery<OnboardingDetailSubscription>(keys.onboardingSubscriptionDetail(id), {
    fetch: { url: urls.onboardingSubscription(id) },
  });
};

export const useUpdateSubscriptionMutation = (
  id: number,
  { onSuccess }: { onSuccess: () => void },
) => {
  const queryClient = useQueryClient();
  const invalidateClient = useInvalidateClient();
  return useFetchMutation<
    SubscriptionUpdate,
    void,
    void,
    SubscriptionUpdate,
    // PaymentErrorData only applicable for OrderNow actions
    FetchError<SubscriptionUpdate, PaymentErrorData>
  >({
    fetch: (body) => ({ method: "PATCH", url: urls.subscription(id), body }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.subscriptions() });
        // client detail shows the active/paused subscriptions for each address
        invalidateClient();
        onSuccess();
      },
    },
  });
};

/**
 * Whether the client can be offered a retention discount for this autoship, and what that
 * discount would do to their next order.
 */
export const useSubscriptionRetentionOfferQuery = (
  id: number,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<RetentionOfferPreview>(keys.subscriptionRetentionOffer(id), {
    fetch: { url: urls.subscriptionRetentionOffer(id) },
    config: { enabled },
  });
};

export const useRecordRetentionOfferDecisionMutation = (id: number) => {
  const queryClient = useQueryClient();
  return useFetchMutation<RetentionOfferDecisionUpdate, RetentionOfferDecision>({
    fetch: (body) => ({ method: "PUT", url: urls.subscriptionRetentionOffer(id), body }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.subscriptions() });
      },
    },
  });
};

export const useUpdateSubscriptionPaymentMethodMutation = (
  id: number,
  {
    onSuccess = noop,
    onError = noop,
  }: {
    onSuccess?: (data: SubscriptionPaymentUpdateResponse) => void;
    onError?: (
      error: FetchError<SubscriptionPaymentUpdate, PaymentErrorData>,
      data: SubscriptionPaymentUpdate,
    ) => void;
  },
) => {
  const queryClient = useQueryClient();
  return useFetchMutation<
    SubscriptionPaymentUpdate,
    SubscriptionPaymentUpdateResponse,
    SubscriptionPaymentUpdateResponse,
    SubscriptionPaymentUpdate,
    FetchError<SubscriptionPaymentUpdate, PaymentErrorData>
  >({
    fetch: (body) => ({
      method: "PATCH",
      url: urls.subscriptionPayment(id),
      body,
    }),
    config: {
      // Awaited so the mutation stays pending until the refetched subscription lands. The
      // response carries no payment method, so SubscriptionPaymentAlert — which stacks this
      // drawer over the form it is blocking — would otherwise reappear over the form it
      // returns to, against a card that is already fixed.
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({ queryKey: keys.subscriptions() });
        onSuccess(data);
      },
      onError,
    },
  });
};

export const useUpdateBulkSubscriptionsMutation = ({
  onSuccess = noop,
  onError = noop,
}: {
  onSuccess?: () => void;
  onError?: (
    error: FetchError<BulkUpdateSubscriptions, PaymentErrorData>,
    data: BulkUpdateSubscriptions,
  ) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const invalidateClient = useInvalidateClient();
  return useFetchMutation<
    BulkUpdateSubscriptions,
    BulkUpdateSubscriptionsResponse,
    void,
    BulkUpdateSubscriptions,
    FetchError<BulkUpdateSubscriptions, PaymentErrorData>
  >({
    fetch: (body) => ({
      method: "POST",
      url: urls.bulkUpdateSubscriptions(),
      body,
    }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.subscriptions() });
        invalidateClient();
        onSuccess();
      },
      onError,
    },
  });
};
